import type { Outcome, OutcomeStatus, Resolution } from '@/lib/api/event/models';
import type { OutcomePrices } from '@/lib/store/prices-store';
import type { MarketItem } from '@/lib/types/markets/market';

// Yes-leg price = the outcome's chance of happening. Find by the
// authoritative `isPositive` flag from the backend, never by array index.
// `resolutionPrices` is a positional projection that also drops nulls,
// so `[0]` can silently point at the No-leg or get shifted by a missing
// price upstream — both produce wildly wrong "100% Yes" / "0%" displays.
// For a closed outcome this is the price event-service stored when trading
// stopped (1 / 0 / 0.5 once resolved); null if it never stored one (shown as 0).
export function getYesLegPrice(item: { r?: Resolution[] }): number | null {
  return item.r?.find((res) => res.isPositive === true || res.name === 'Yes')?.price ?? null;
}

// Once trading stopped (an `ended` event, or an outcome no longer ACTIVE) only
// the stored closing price counts; a live outcome falls back to the best ask,
// then the last trade.
export function getYesPrice(item: Outcome, ended = false): number {
  const stored = getYesLegPrice(item);
  if (ended || !isOutcomeActive(item)) return stored ?? 0;
  return stored ?? item.bestAsk ?? item.lastTradePrice ?? 0;
}

// Whole-percent label of a 0..1 Yes price. The `no` side is 100 − the rounded
// Yes, so a Yes/No pair always adds up to 100.
export function formatChance(price: number, side: 'yes' | 'no' = 'yes'): string {
  const yes = Math.round(price * 100);
  return `${side === 'yes' ? yes : 100 - yes}%`;
}

// Only ACTIVE outcomes are tradeable; the rest (PAUSED, AWAITING_RESOLUTION,
// RESOLVED) are inactive. Missing status is treated as active to stay safe
// against older payloads that omit it. Accepts any status-bearing shape so it
// works for both `Outcome` and `MarketItem`.
export function isOutcomeActive(item: { status?: OutcomeStatus | null }): boolean {
  return item.status == null || item.status === 'ACTIVE';
}

// An ended event keeps its outcomes ACTIVE, so an outcome is tradable only
// while this is false AND isOutcomeActive holds. Primary: the server's
// `active` flag; fallback for payloads without it: the end date has passed.
export function isMarketEnded(market: { active?: boolean; endDate?: string }): boolean {
  if (market.active === false) return true;
  if (market.active === undefined && market.endDate) {
    return new Date(market.endDate).getTime() < Date.now();
  }
  return false;
}

// Order outcomes by Yes-price (chance) descending, pushing inactive ones to the
// bottom regardless of their price. Returns a new array; input is not mutated.
export function sortOutcomesByChance<T extends Outcome>(items: T[]): T[] {
  return items.slice().sort((a, b) => {
    const aActive = isOutcomeActive(a);
    const bActive = isOutcomeActive(b);
    if (aActive !== bActive) return aActive ? -1 : 1;
    return getYesPrice(b) - getYesPrice(a);
  });
}

// Prices to render before the prices store is hydrated — i.e. in the server
// HTML and on the first client paint. Derived from the outcome payload, which
// the page enriches with the order-book snapshot (see applyOrderBookPrices),
// so crawlers see the same chance and Yes/No quotes a visitor does.
export function outcomeFallbackPrices(item: MarketItem): OutcomePrices {
  const chance = Number(item.outcomePrices[0] ?? 0);
  return {
    chance: Number.isFinite(chance) ? chance : 0,
    bestAsk: item.bestAsk > 0 ? item.bestAsk : null,
    bestBid: typeof item.bestBid === 'number' && item.bestBid > 0 ? item.bestBid : null,
  };
}
